import React from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Button } from 'primereact/button'
import { Toast } from 'primereact/toast'
import { Toolbar } from 'primereact/toolbar'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { pageShops, deleteShop } from '../../api/shops'
import { useNavigate } from 'react-router-dom'
import ClassicAppBar from '../../components/ClassicAppBar'
import BreadcrumbsNav from '../../components/BreadcrumbsNav'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../../store/store'
import { setLanguage } from '../../store/slices/languageSlice'
import { toggleMode } from '../../store/slices/themeSlice'
import { useTranslation } from 'react-i18next'

const ShopsAdmin: React.FC = () => {
  const [shops, setShops] = React.useState<any[]>([])
  const [globalFilter, setGlobalFilter] = React.useState<string>('')
  const [deleteDialog, setDeleteDialog] = React.useState<boolean>(false)
  const [shopToDelete, setShopToDelete] = React.useState<any>(null)
  const toast = React.useRef<Toast>(null)
  const navigate = useNavigate()
  const { i18n } = useTranslation()
  const dispatch = useDispatch()
  const themeMode = useSelector((state: RootState) => state.theme?.mode || 'light')

  const handleLanguageChange = (lang: 'en' | 'ta' | 'de') => {
    i18n.changeLanguage(lang)
    dispatch(setLanguage(lang))
  }

  const handleToggleMode = () => {
    dispatch(toggleMode())
  }

  const loadData = async () => {
    try {
      const data = await pageShops({ page: 0, size: 50 })
      setShops(data.content || [])
    } catch (error) {
      console.error('Failed to load shops:', error)
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load shops' })
    }
  }

  React.useEffect(() => {
    loadData()
  }, [])

  const confirmDelete = (shop: any) => {
    setShopToDelete(shop)
    setDeleteDialog(true)
  }

  const deleteShopAction = async () => {
    try {
      await deleteShop(shopToDelete.id)
      setDeleteDialog(false)
      setShopToDelete(null)
      loadData()
      toast.current?.show({ severity: 'success', summary: 'Successful', detail: 'Shop Deleted', life: 3000 })
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: error.message || 'Delete failed', life: 3000 })
    }
  }

  const leftToolbarTemplate = () => {
    return (
      <React.Fragment>
        <div className="text-2xl font-bold">Shops Management</div>
      </React.Fragment>
    )
  }

  const rightToolbarTemplate = () => {
    return (
      <React.Fragment>
        <Button label="New" icon="pi pi-plus" className="p-button-success mr-2" onClick={() => navigate('/admin/shops/new')} />
      </React.Fragment>
    )
  }

  const actionBodyTemplate = (rowData: any) => {
    return (
      <React.Fragment>
        <Button icon="pi pi-pencil" className="p-button-rounded p-button-success mr-2" onClick={() => navigate(`/admin/shops/${rowData.id}/edit`)} />
        <Button icon="pi pi-trash" className="p-button-rounded p-button-warning" onClick={() => confirmDelete(rowData)} />
      </React.Fragment>
    )
  }

  const header = (
    <div className="flex flex-wrap gap-2 align-items-center justify-content-between">
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText type="search" onInput={(e) => setGlobalFilter(e.currentTarget.value)} placeholder="Search..." />
      </span>
    </div>
  )

  const deleteDialogFooter = (
    <React.Fragment>
      <Button label="No" icon="pi pi-times" className="p-button-text" onClick={() => setDeleteDialog(false)} />
      <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteShopAction} />
    </React.Fragment>
  )

  return (
    <div className="min-h-screen bg-gray-50 flex flex-column">
      <ClassicAppBar
        mode={themeMode}
        onToggleMode={handleToggleMode}
        onChangeLang={handleLanguageChange}
      />
      <div className="container mx-auto p-4 lg:p-6 max-w-7xl">
        <BreadcrumbsNav items={[{ label: 'Admin', to: '/admin' }, { label: 'Shops' }]} />

        <div className="card mt-4">
          <Toolbar className="mb-4" left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>

          <DataTable value={shops} paginator rows={10} rowsPerPageOptions={[5, 10, 25]}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} shops"
            globalFilter={globalFilter} header={header} responsiveLayout="scroll">
            <Column field="name" header="Name" sortable style={{ minWidth: '12rem' }}></Column>
            <Column field="type" header="Type" sortable style={{ minWidth: '10rem' }}></Column>
            <Column field="city" header="City" sortable style={{ minWidth: '10rem' }}></Column>
            <Column field="country" header="Country" sortable style={{ minWidth: '10rem' }}></Column>
            <Column body={actionBodyTemplate} exportable={false} style={{ minWidth: '8rem' }}></Column>
          </DataTable>
        </div>
      </div>

      <Dialog visible={deleteDialog} style={{ width: '32rem' }} breakpoints={{ '960px': '75vw', '641px': '90vw' }} header="Confirm" modal footer={deleteDialogFooter} onHide={() => setDeleteDialog(false)}>
        <div className="confirmation-content">
          <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
          {shopToDelete && <span>Are you sure you want to delete <b>{shopToDelete.name}</b>?</span>}
        </div>
      </Dialog>
      <Toast ref={toast} />
    </div>
  )
}

export default ShopsAdmin
